'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useState } from 'react';
import { OutcomeDot } from '@/components/shared/outcome-indicator';
import { AppPopover } from '@/components/ui/app-popover';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { type OutcomeHueName, outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';
import { MARKET_TAB_FILTER_TRIGGER_CLASS } from '../market-tab-filter-trigger';
interface OutcomeOption {
  index: number;
  label: string;
  imageUrl?: string | undefined;
}
interface MarketTradersOutcomeFilterProps {
  /** Parent market pubkey — seeds the outcome indicator's pattern lib. */
  marketPubkey: string;
  outcomes: readonly OutcomeOption[];
  /** Currently selected outcome index. `null` = "All outcomes" (market aggregate). */
  selectedIndex: number | null;
  /** Pick a single outcome (or `null` for All). */
  onSelect: (index: number | null) => void;
}

/**
 * Single-select outcome filter for the per-market traders leaderboard.
 */
export function MarketTradersOutcomeFilter(t0) {
  const $ = _c(51);
  const {
    outcomes,
    selectedIndex,
    onSelect
  } = t0;
  const tMarket = useTranslations("market.traders");
  const tTrade = useTranslations("trade");
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== outcomes) {
    t1 = outcomes.map(_temp);
    $[0] = outcomes;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const colorClasses = useOutcomeColor(t1);
  let colorByIndex;
  if ($[2] !== colorClasses || $[3] !== outcomes) {
    colorByIndex = {};
    const hueByIndex = {};
    for (let i = 0; i < outcomes.length; i++) {
      const o_0 = outcomes[i];
      const cls = colorClasses[i];
      if (o_0 !== undefined && cls !== undefined) {
        colorByIndex[o_0.index] = outcomeHueToStepVar(cls.hue, 400);
        hueByIndex[o_0.index] = cls.hue;
      }
    }
    $[2] = colorClasses;
    $[3] = outcomes;
    $[4] = colorByIndex;
  } else {
    colorByIndex = $[4];
  }
  if (outcomes.length <= 1) {
    return null;
  }
  let t2;
  if ($[5] !== outcomes || $[6] !== selectedIndex) {
    t2 = selectedIndex !== null ? outcomes.find(o_1 => o_1.index === selectedIndex) : undefined;
    $[5] = outcomes;
    $[6] = selectedIndex;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const selected = t2;
  let t3;
  if ($[8] !== tMarket) {
    t3 = tMarket("outcome_filter_label");
    $[8] = tMarket;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== colorByIndex || $[11] !== selected || $[12] !== tTrade) {
    t4 = <span className="flex min-w-0 items-center gap-2 truncate">{selected === undefined ? <span className="truncate text-foreground-secondary">{tTrade("all_outcomes")}</span> : <><OutcomeDot color={colorByIndex[selected.index] ?? ""} /><span className="truncate">{selected.label}</span></>}</span>;
    $[10] = colorByIndex;
    $[11] = selected;
    $[12] = tTrade;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={ArrowDown01Icon} className="size-4 shrink-0 opacity-50" />;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== open || $[16] !== t3 || $[17] !== t4) {
    t6 = <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={t3} data-testid="market-traders-outcome-filter" className={MARKET_TAB_FILTER_TRIGGER_CLASS}>{t4}{t5}</button>;
    $[15] = open;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const trigger = t6;
  const t7 = selectedIndex === null;
  let t8;
  if ($[19] !== onSelect) {
    t8 = () => {
      onSelect(null);
      setOpen(false);
    };
    $[19] = onSelect;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = selectedIndex === null ? "bg-background-tertiary font-medium text-foreground-primary" : "text-foreground-secondary hover:bg-background-tertiary/50 hover:text-foreground-primary";
  let t10;
  if ($[21] !== t9) {
    t10 = cn("flex w-full items-center justify-between px-3 py-2.5 text-left text-body transition-colors", t9);
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== tTrade) {
    t11 = tTrade("all_outcomes");
    $[23] = tTrade;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <span className="truncate">{t11}</span>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== selectedIndex) {
    t13 = selectedIndex === null && <Icon icon={Tick02Icon} className="size-4 shrink-0" />;
    $[27] = selectedIndex;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t13 || $[32] !== t7 || $[33] !== t8) {
    t14 = <button type="button" aria-pressed={t7} onClick={t8} className={t10}>{t12}{t13}</button>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
    $[32] = t7;
    $[33] = t8;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== colorByIndex || $[36] !== onSelect || $[37] !== outcomes || $[38] !== selectedIndex) {
    let t16;
    if ($[40] !== colorByIndex || $[41] !== onSelect || $[42] !== selectedIndex) {
      t16 = outcome => {
        const isSelected = selectedIndex === outcome.index;
        const dotColor = colorByIndex[outcome.index] ?? "";
        return <button key={outcome.index} type="button" aria-pressed={isSelected} data-testid={`market-traders-outcome-item-${outcome.index}`} onClick={() => {
          onSelect(outcome.index);
          setOpen(false);
        }} className={cn("flex w-full items-center justify-between px-3 py-2.5 text-left text-body transition-colors", isSelected ? "bg-background-tertiary font-medium text-foreground-primary" : "text-foreground-secondary hover:bg-background-tertiary/50 hover:text-foreground-primary")}><span className="flex min-w-0 items-center gap-2.5 truncate"><OutcomeDot color={dotColor} /><span className="truncate">{outcome.label}</span></span>{isSelected && <Icon icon={Tick02Icon} className="size-4 shrink-0" />}</button>;
      };
      $[40] = colorByIndex;
      $[41] = onSelect;
      $[42] = selectedIndex;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    t15 = outcomes.map(t16);
    $[35] = colorByIndex;
    $[36] = onSelect;
    $[37] = outcomes;
    $[38] = selectedIndex;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[44] !== t14 || $[45] !== t15) {
    t16 = <div className="max-h-56 overflow-y-auto">{t14}{t15}</div>;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const content = t16;
  let t17;
  if ($[47] !== content || $[48] !== open || $[49] !== trigger) {
    t17 = <AppPopover open={open} onOpenChange={setOpen} trigger={trigger} content={content} side="bottom" align="end" sideOffset={4} contentClassName="w-[var(--radix-popover-trigger-width)] min-w-56 bg-background-primary p-0" />;
    $[47] = content;
    $[48] = open;
    $[49] = trigger;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.index
  };
}